import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { DialogCloseButton } from '../../../../components/core/DialogCloseButton';
import { BaseStatusChip } from '../../../Paylinks/components/StatusChip';
import { WebhookStatusChip } from '../WebhookStatusChip';
import { EventNotificationAttemptType, WebhookEventDataType } from '../../types/types';
import { colorError, colorSuccess } from '../../../../util/constants';

dayjs.extend(utc);

const EventRelatedInfo = ({
  children,
  title,
  fullWidth = false,
}: {
  children: React.ReactNode;
  title: string;
  fullWidth?: boolean;
}) => (
  <Stack spacing={1}>
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 600,
        color: (theme) => theme.palette.grey[700],
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        '& p': {
          fontSize: '14px',
          lineHeight: '18px',
          ...(fullWidth
            ? { overflowWrap: 'anywhere' }
            : {
                maxWidth: { xs: '130px', sm: '200px', md: '220px' },
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }),
        },
      }}
    >
      {children}
    </Box>
  </Stack>
);

const getAttemptStatusColor = (status: string) => {
  return status === 'SUCCESS' ? colorSuccess : colorError;
};

const UNREACHABLE_RESPONSE_CODE_TOOLTIP =
  "No response code means we couldn't reach your server. This usually indicates a network error, DNS failure, or timeout.";

const ResponseCodeCell = ({ responseCode }: { responseCode: number }) => {
  if (responseCode !== 0) {
    return <>{responseCode}</>;
  }

  return (
    <Tooltip title={UNREACHABLE_RESPONSE_CODE_TOOLTIP} arrow placement="top">
      <Typography component="span" sx={{ cursor: 'help' }}>
        —
      </Typography>
    </Tooltip>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  event: WebhookEventDataType;
}

type AttemptRow = {
  attemptNumber: number;
  attempt: EventNotificationAttemptType;
};

export const WebhookEventResponsesDialog = ({ isOpen, onClose, event }: Props) => {
  const attemptsNewestFirst: AttemptRow[] = useMemo(() => {
    const { notificationAttempts } = event;
    const total = notificationAttempts.length;

    return notificationAttempts.map((attempt, index) => ({
      attemptNumber: total - index,
      attempt,
    }));
  }, [event]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Webhook Responses</DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent dividers>
        <Stack spacing={4}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: (theme) => theme.palette.grey[700],
            }}
          >
            The following responses were returned from your endpoint for this webhook event:
          </Typography>
          <Stack spacing={2}>
            <EventRelatedInfo title="Endpoint URL" fullWidth>
              <Typography>{event.endpointUrl}</Typography>
            </EventRelatedInfo>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr 1fr',
                  sm: '1fr 1fr',
                  md: 'repeat(5, minmax(0, 1fr))',
                },
                columnGap: { xs: 2, sm: 4, md: 4 },
                rowGap: 2,
              }}
            >
              <EventRelatedInfo title="Status">
                <WebhookStatusChip webhookEvent={event} size="small" />
              </EventRelatedInfo>
              <EventRelatedInfo title="Event">
                <Typography>{event.eventType.eventDescription}</Typography>
              </EventRelatedInfo>
              <EventRelatedInfo title="Date">
                <Typography>{dayjs.utc(event.created).format('MM/DD/YY')}</Typography>
              </EventRelatedInfo>
              <EventRelatedInfo title="Time (UTC)">
                <Typography>{dayjs.utc(event.created).format('h:mm:ss a')}</Typography>
              </EventRelatedInfo>
              <EventRelatedInfo title="Total attempts">
                <Typography>{event.notificationAttemptCount}</Typography>
              </EventRelatedInfo>
            </Box>
          </Stack>
          <TableContainer component={Box} sx={{ position: 'relative', minHeight: '120px' }}>
            <Table
              sx={{
                '& th': {
                  fontWeight: 600,
                  fontSize: '12px',
                  border: 0,
                },
                '& tr': {
                  height: '50px',
                },
                '& td': {
                  fontSize: '13px',
                  verticalAlign: 'top',
                },
              }}
              aria-label="Webhook response attempts"
            >
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  background: (theme) => theme.palette.grey[100],
                  padding: 2.5,
                  zIndex: 1,
                }}
              >
                <TableRow>
                  <TableCell component="th">Attempt</TableCell>
                  <TableCell component="th">Date (UTC)</TableCell>
                  <TableCell component="th">Time (UTC)</TableCell>
                  <TableCell component="th">Status</TableCell>
                  <TableCell component="th">Response code</TableCell>
                  <TableCell component="th">Response body</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attemptsNewestFirst.map(({ attemptNumber, attempt }) => (
                  <TableRow key={attemptNumber}>
                    <TableCell scope="row">{attemptNumber}</TableCell>
                    <TableCell>{dayjs.utc(attempt.created).format('MM/DD/YY')}</TableCell>
                    <TableCell>{dayjs.utc(attempt.created).format('h:mm:ss a')}</TableCell>
                    <TableCell>
                      <BaseStatusChip
                        color={getAttemptStatusColor(attempt.notificationAttemptStatus)}
                        label={attempt.notificationAttemptStatus}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <ResponseCodeCell responseCode={attempt.responseCode} />
                    </TableCell>
                    <TableCell>
                      {attempt.failureMessage ? (
                        <Box
                          component="code"
                          sx={{
                            display: 'block',
                            maxHeight: '120px',
                            overflow: 'auto',
                            p: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            backgroundColor: 'grey.50',
                            borderRadius: '4px',
                            fontSize: '12px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {attempt.failureMessage}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {attemptsNewestFirst.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography
                        sx={{
                          textAlign: 'center',
                          color: (theme) => theme.palette.grey[700],
                          fontStyle: 'italic',
                          py: 2,
                        }}
                      >
                        No delivery attempts recorded yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookEventResponsesDialog;
